import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wrench, Wind, Volume2, Thermometer, Building2, Settings, ArrowRight, Check, ChevronRight } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Approche (content from HomePage ExpertiseSection)
const approachCards = [
  { icon: Wrench,      title: "Installation spécialisée", desc: "Pose professionnelle de systèmes HVAC adaptés à chaque environnement." },
  { icon: Wind,        title: "Solutions sur mesure",     desc: "Systèmes adaptés aux contraintes spécifiques de chaque projet." },
  { icon: Volume2,     title: "Performance acoustique",   desc: "Installation silencieuse pour studios et espaces sensibles au bruit." },
  { icon: Thermometer, title: "Confort optimal",          desc: "Régulation thermique précise et qualité d'air intérieur garanties." }
];

// Savoir-faire HVAC (content from HomePage InstallationExpertiseSection)
const savoirFaire = [
  { icon: Wrench,    title: "Installation spécialisée",   description: "Installation de systèmes de climatisation et ventilation pour environnements exigeants : studios, hôtels, cinémas privés." },
  { icon: Volume2,   title: "Environnements sensibles",   description: "Solutions adaptées aux studios d'enregistrement, hôtels, bureaux premium et cinémas privés nécessitant un fonctionnement silencieux." },
  { icon: Building2, title: "Intégration architecturale", description: "Installation discrète et performante dans des projets architecturaux haut de gamme." }
];

// Services (content from HomePage ServicesSection)
const services = [
  { number: "01", icon: Wrench,   title: "Installation climatisation et ventilation", description: "Pose de systèmes haut de gamme : gainables, splits, VRV/VRF, CTA et VMC double flux." },
  { number: "02", icon: Settings, title: "Maintenance et dépannage HVAC",              description: "Contrats de maintenance préventive et interventions d'urgence toute l'année." },
  { number: "03", icon: Wind,     title: "Solutions sur mesure",                       description: "Systèmes adaptés aux contraintes spécifiques de chaque environnement exigeant." }
];

const brands = ["Daikin", "Mitsubishi", "Hitachi", "Bosch"];
const badges = ["Techniciens certifiés", "Garantie décennale", "SAV 7j/7", "Devis gratuit"];

export default function ExpertisePage() {
  return (
    <>
      <Helmet>
        <title>Notre expertise HVAC | FLOW - Climatisation & Ventilation haut de gamme</title>
        <meta name="description" content="Expertise FLOW en installation et maintenance de systèmes HVAC : gainables, VRV/VRF, CTA double flux, VMC. Marques installées : Daikin, Mitsubishi, Hitachi, Bosch." />
        <link rel="canonical" href="https://flow-air.com/expertise" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24" data-testid="expertise-hero">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Notre expertise</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Spécialiste en installation de <span className="text-[#00AFFF]">climatisation haut de gamme</span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              FLOW est une entreprise spécialisée dans l'installation et la maintenance de systèmes de
              climatisation et ventilation haut de gamme. Notre expertise technique nous permet de réaliser
              des installations sur mesure pour les environnements les plus exigeants.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Notre approche */}
      <section className="py-16 md:py-24 bg-white" data-testid="expertise-approche">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Notre approche</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Une expertise technique sur chaque projet
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {approachCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.title}
                  className="p-6 bg-[#F5F7FA] rounded-2xl border border-transparent hover:border-[#00AFFF]/30 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`expertise-approche-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#00AFFF]" />
                  </div>
                  <h3 className="text-base font-semibold mb-2 text-[#0E1A2B]">{card.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{card.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Savoir-faire HVAC */}
      <section className="py-16 md:py-24 bg-[#F5F7FA]" data-testid="expertise-savoirfaire">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="mb-12 max-w-3xl">
            <span className="caption mb-4 block">Savoir-faire</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Notre expertise HVAC
            </h2>
            <p className="text-[#475569] leading-relaxed text-base md:text-lg">
              FLOW accompagne architectes, studios d'enregistrement et professionnels de l'hôtellerie dans
              l'installation de systèmes de climatisation et ventilation sur mesure. Nos installations prennent
              en compte les contraintes acoustiques et architecturales de chaque projet.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {savoirFaire.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-[#E2E8F0]"
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                  data-testid={`expertise-savoirfaire-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center mb-5">
                    <Icon size={22} className="text-[#00AFFF]" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-[#0E1A2B]">{feature.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-white" data-testid="expertise-services">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="mb-12 max-w-2xl">
            <span className="caption mb-4 block">Nos services</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Nos services en climatisation et ventilation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.article
                  key={service.title}
                  className="bg-[#F5F7FA] rounded-2xl p-6 border border-transparent hover:border-[#00AFFF]/30 transition-colors"
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                  data-testid={`expertise-service-${index}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center">
                      <Icon size={22} className="text-[#00AFFF]" />
                    </div>
                    <span className="text-sm font-bold text-[#94A3B8]" style={{ fontFamily: 'Sora, sans-serif' }}>{service.number}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-[#0E1A2B]">{service.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed mb-5">{service.description}</p>
                  <Link to="/contact" className="text-[#00AFFF] text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                    En savoir plus <ChevronRight size={16} />
                  </Link>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="py-12 md:py-16 bg-[#F5F7FA]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center">
            <span className="caption mb-6 block">Marques installées</span>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {brands.map((brand) => (
                <span
                  key={brand}
                  className="text-lg font-bold tracking-wider text-[#94A3B8] hover:text-[#0E1A2B] transition-colors"
                >
                  {brand.toUpperCase()}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Badges globaux */}
      <section className="py-12 bg-white">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {badges.map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-[#F5F7FA] rounded-lg">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center">
                  <Check size={12} className="text-[#00AFFF]" />
                </div>
                <span className="text-xs font-medium text-[#0E1A2B]">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0E1A2B]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Parlons de votre projet
            </h2>
            <p className="text-[#94A3B8] mb-8 leading-relaxed">
              Contactez l'équipe FLOW pour échanger sur votre besoin en climatisation et ventilation.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="expertise-cta-contact">
              Demander un devis <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
